import { ApiError } from '../../shared/api/ApiClient.js';
import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { platformJsonHeaders } from '../../shared/http/platformEnvelopeLocale.js';
import type {
  PublicDiscoveryHomeCategoriesResultDTO,
  PublicDiscoverySearchResultDTO,
  PublicPlatformCategoriesResultDTO,
} from './publicDiscoveryTypes.js';

export const PUBLIC_DISCOVERY_VALIDATION_ERROR_CODE = 'VALIDATION_ERROR';

interface PublicDiscoverySuccessEnvelope<T> {
  success: true;
  data: T;
}

interface PublicDiscoveryErrorEnvelope {
  success: false;
  error?: string;
  code?: string;
  errors?: Array<{ field?: string; message?: string }>;
}

export interface FetchPublicDiscoverySearchOptions {
  readonly q?: string | null;
  readonly platformCategory?: string | null;
  readonly lat?: number | null;
  readonly lng?: number | null;
  readonly radius?: number | null;
  readonly sort?: 'relevance' | 'distance';
  readonly cursor?: string | null;
  readonly limit?: number;
  readonly locale?: string;
  readonly signal?: AbortSignal;
}

export interface FetchPublicPlatformCategoriesOptions {
  readonly locale?: string;
  readonly signal?: AbortSignal;
}

function trimBaseUrl(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/+$/, '');
}

function appendIfPresent(params: URLSearchParams, key: string, value: string | null | undefined): void {
  if (typeof value !== 'string') {
    return;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return;
  }
  params.set(key, trimmed);
}

function appendNumberIfFinite(
  params: URLSearchParams,
  key: string,
  value: number | null | undefined,
): void {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return;
  }
  params.set(key, String(value));
}

function appendLocationShape(
  params: URLSearchParams,
  options: Pick<FetchPublicDiscoverySearchOptions, 'lat' | 'lng' | 'radius'>,
): void {
  const hasLat = typeof options.lat === 'number' && Number.isFinite(options.lat);
  const hasLng = typeof options.lng === 'number' && Number.isFinite(options.lng);
  const hasRadius = typeof options.radius === 'number' && Number.isFinite(options.radius);
  if (!hasLat || !hasLng || !hasRadius) {
    return;
  }
  params.set('lat', String(options.lat));
  params.set('lng', String(options.lng));
  params.set('radius', String(options.radius));
}

async function readEnvelope<T>(
  response: Response,
): Promise<PublicDiscoverySuccessEnvelope<T> | PublicDiscoveryErrorEnvelope> {
  try {
    return (await response.json()) as PublicDiscoverySuccessEnvelope<T> | PublicDiscoveryErrorEnvelope;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }
}

function toValidationError(status: number, body: PublicDiscoveryErrorEnvelope): ApiError {
  return new ApiError(
    resolvePublicApiFailureMessage({
      status,
      code: body.code,
      rawMessage: typeof body.error === 'string' ? body.error : undefined,
    }),
    status,
    body.code,
    body,
  );
}

async function fetchDiscoveryEnvelope<T>(
  url: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await stormAwareFetch(url, {
    method: 'GET',
    headers: platformJsonHeaders(),
    signal,
  });

  const body = await readEnvelope<T>(response);
  if (!response.ok || body.success !== true) {
    const errorBody: PublicDiscoveryErrorEnvelope =
      body.success === false
        ? body
        : {
            success: false,
            error: 'Unexpected discovery response envelope.',
          };
    if (
      response.status === 422 &&
      errorBody.code === PUBLIC_DISCOVERY_VALIDATION_ERROR_CODE
    ) {
      throw toValidationError(response.status, errorBody);
    }
    throw customerEnvelopeApiError(response.status, errorBody);
  }

  return body.data;
}

export async function fetchPublicDiscoverySearch(
  apiBaseUrl: string,
  options: FetchPublicDiscoverySearchOptions = {},
): Promise<PublicDiscoverySearchResultDTO> {
  const base = trimBaseUrl(apiBaseUrl);
  const params = new URLSearchParams();
  appendIfPresent(params, 'q', options.q);
  appendIfPresent(params, 'platformCategory', options.platformCategory);
  appendIfPresent(params, 'cursor', options.cursor);
  appendIfPresent(params, 'locale', options.locale);
  appendNumberIfFinite(params, 'limit', options.limit);
  if (options.sort === 'distance') {
    params.set('sort', 'distance');
  } else if (options.sort === 'relevance') {
    params.set('sort', 'relevance');
  }
  appendLocationShape(params, options);

  const query = params.toString();
  return fetchDiscoveryEnvelope<PublicDiscoverySearchResultDTO>(
    `${base}/api/v1/public/discovery/search${query.length > 0 ? `?${query}` : ''}`,
    options.signal,
  );
}

export async function fetchPublicPlatformCategories(
  apiBaseUrl: string,
  options: FetchPublicPlatformCategoriesOptions = {},
): Promise<PublicPlatformCategoriesResultDTO> {
  const base = trimBaseUrl(apiBaseUrl);
  const params = new URLSearchParams();
  appendIfPresent(params, 'locale', options.locale);
  const query = params.toString();
  return fetchDiscoveryEnvelope<PublicPlatformCategoriesResultDTO>(
    `${base}/api/v1/public/discovery/platform-categories${query.length > 0 ? `?${query}` : ''}`,
    options.signal,
  );
}

export async function fetchPublicPlatformCategoriesTree(
  apiBaseUrl: string,
  options: FetchPublicPlatformCategoriesOptions = {},
): Promise<PublicPlatformCategoriesResultDTO> {
  const base = trimBaseUrl(apiBaseUrl);
  const params = new URLSearchParams();
  appendIfPresent(params, 'locale', options.locale);
  const query = params.toString();
  return fetchDiscoveryEnvelope<PublicPlatformCategoriesResultDTO>(
    `${base}/api/v1/public/discovery/platform-categories/tree${query.length > 0 ? `?${query}` : ''}`,
    options.signal,
  );
}

export async function fetchPublicDiscoveryHomeCategories(
  apiBaseUrl: string,
  options: FetchPublicPlatformCategoriesOptions = {},
): Promise<PublicDiscoveryHomeCategoriesResultDTO> {
  const base = trimBaseUrl(apiBaseUrl);
  const params = new URLSearchParams();
  appendIfPresent(params, 'locale', options.locale);
  const query = params.toString();
  return fetchDiscoveryEnvelope<PublicDiscoveryHomeCategoriesResultDTO>(
    `${base}/api/v1/public/discovery/home-categories${query.length > 0 ? `?${query}` : ''}`,
    options.signal,
  );
}

/**
 * NEW G7 — resolve Home promo media via public stream (never bannerImageUrl / signed URL).
 * `variant: 'thumbnail'` uses optional thumbnail key when present on the server.
 */
export function buildPublicDiscoveryHomeBannerStreamUrl(
  apiBaseUrl: string,
  variant: 'full' | 'thumbnail' = 'full',
): string {
  const base = trimBaseUrl(apiBaseUrl);
  const path =
    variant === 'thumbnail'
      ? '/api/v1/public/discovery/home-banner/thumbnail'
      : '/api/v1/public/discovery/home-banner';
  return `${base}${path}`;
}

export function isPublicDiscoveryCursorValidationError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  if (error.code !== PUBLIC_DISCOVERY_VALIDATION_ERROR_CODE) {
    return false;
  }
  const body = error.data;
  if (body === null || typeof body !== 'object') {
    return false;
  }
  const errors = (body as { errors?: Array<{ field?: string; message?: string }> }).errors;
  if (!Array.isArray(errors)) {
    return false;
  }
  return errors.some((entry) => entry?.field === 'cursor');
}
